<template>
  <div :style="getCSS()">
    <!--    Loader-->
    <loading-panel :loading="loading"></loading-panel>

    <!--    Every dashboard in this group is restricted and none is shared with this user (OTC-3138)-->
    <card v-if="noDashboards && !loading">
      <div class="empty-state">
        <div class="empty-state__icon"><i class="tim-icons icon-chart-bar-32"></i></div>
        <h3 class="empty-state__title">No dashboards are shared with you</h3>
        <p class="empty-state__hint">Ask an admin or a manager to give your role or your account access to a dashboard.</p>
        <div v-if="hasPermissions('dashboards_update')" class="empty-state__actions">
          <base-button variant="outlined" tone="primary" size="sm" @click="goToDashboardSettings()">Manage dashboards</base-button>
        </div>
      </div>
    </card>

    <div class="row" v-show="!loading && !noDashboards">
      <div class="col-12 col-md dashboard-heading">
        <h2 class="dashboard-heading-title">{{dashboard.name}}</h2>
        <p v-if="dashboard.description" class="dashboard-heading-description">{{dashboard.description}}</p>
      </div>
      <div class="col-12 col-md text-md-right dashboard-heading-actions">
        <el-tooltip content="Refresh dashboard"
                    effect="light"
                    :open-delay="300"
                    placement="top">
          <base-button link v-on:click="getMainDashboard(dashboard.id)"  style="font-size: large">
            <i class="tim-icons icon-refresh-01 text-white"></i>
          </base-button>
        </el-tooltip>
        <el-tooltip content="Full screen"
                    effect="light"
                    :open-delay="300"
                    placement="top">
            <i v-if="!fullscreen"
               v-on:click="goToFullScreen(dashboard.id)"
               style="cursor: pointer; font-size: large"
               class="tim-icons el-icon-full-screen text-white"></i>
        </el-tooltip>
        <el-tooltip content="Minimize screen"
                    effect="light"
                    :open-delay="300"
                    placement="top">
          <i v-if="fullscreen" v-on:click="goToNormalScreen(dashboard.id)"
             style="cursor: pointer; font-size: large"
             class="tim-icons el-icon-bottom-left text-white"></i>
        </el-tooltip>
        <el-dropdown v-if="dashboards.length > 1">
          <base-button type="primary" link size="sm">More dashboards
            <i class="tim-icons icon-minimal-down ml-2"></i>
          </base-button>
          <template #dropdown>
            <el-dropdown-menu>
              <a v-for="(d, i) in dashboards" v-on:click="getMainDashboard(d.id, $event)" :key="'D' + i" class="dropdown-item mt-2 mb-2" href="#">
                {{ d.name }}
              </a>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
        <base-button class="ml-2" v-if="hasPermissions('dashboards_update')" type="primary" size="sm" v-on:click="goToDashboardSettings()">
          <i class="tim-icons icon-settings-gear-63 mr-2"></i> Dashboard settings
        </base-button>
      </div>
    </div>

    <div class="row" v-if="filterBarConfig.enabled && filterSchema.length > 0" v-show="!loading && !noDashboards">
      <div class="col dashboard-filter-bar">
        <filter-bar :key="'gfb-' + filterBarKey"
                    :filters="filterSchema"
                    :initial-value="filterValues"
                    :loading="filtersLoading"
                    :quick-actions="[{ key: 'reset', label: 'Reset to dashboard defaults', end: true }]"
                    @change="onGlobalFilterChange"
                    @quick-action="onGlobalFilterQuickAction" />
        <info-hint v-if="filterBarConfig.keys.includes('promo') && filterOptions && filterOptions.promos_truncated" variant="neutral" class="mt-2">
          Showing the first 500 promo codes. Select a promo code group to cover every code in it.
        </info-hint>
      </div>
    </div>

    <div class="row" v-if="!noDashboards">
      <div class="col">
        <grid-layout :layout="gridLayout"
                     :col-num="gridCols"
                     :row-height="10"
                     :vertical-compact="true"
                     :use-css-transforms="true"
        >
          <grid-item v-for="item in gridLayout"
                     @resize="onWidgetResized"
                     @resized="onWidgetResized"
                     :static="true"
                     :x="item.x"
                     :y="item.y"
                     :w="item.w"
                     :h="item.h"
                     :i="item.i"
                     :key="item.i + 'GRID'"
          >
            <div v-if="item.widget_type_id === 4" class="widget-section" :style="{ borderColor: item.highlight_color || '' }">
              <div class="widget-section-text">
                <h3 class="widget-section-title">{{ item.title }}</h3>
                <p v-if="item.label" class="widget-section-caption">{{ item.label }}</p>
              </div>
            </div>
            <card v-else :style="{'border-top': '3px solid ' + item.highlight_color }"
                  :data-widget-id="'widget-' + item.id"
                  style="border-radius: 3px; overflow: hidden; height: 100%">
              <!-- Widget Loading Indicator -->
              <div v-if="widgetLoadingStates[item.id]" style="display: flex; justify-content: center; align-items: center; height: 100%; min-height: 150px;">
                <div style="text-align: center;">
                  <i class="tim-icons icon-refresh-01 spin-animation" style="font-size: 2rem;"></i>
                  <p class="text-muted mt-2">Loading widget data...</p>
                </div>
              </div>
              <div v-else class="widget-tile-body" style="position: relative;">
                <div class="row mt-2" v-if="item.show_range_option && !item.is_all_time">
                  <div class="col" style="display: flex; flex-wrap: wrap;">
                    <base-input v-if="!globalPeriodOn" label="Showing results for" style="max-width: 200px;flex: 1">
                      <el-select
                        class="select-primary mb-3 pagination-select"
                        v-model="item.period" v-on:change="getWidgetData(item)"
                        placeholder="Date range"
                        value-key="id"
                      >
                        <el-option
                          class="select-primary"
                          v-for="(i, index) in time_period_types"
                          :key="'IS' + index + j"
                          :label="i.name"
                          :value="i"
                        >
                        </el-option>
                      </el-select>
                    </base-input>
                    <base-input class="ml-2" v-if="!globalPeriodOn && item.period && item.period.id === 16"
                                style="flex: 1" label="Start date">
                      <el-date-picker
                        style="border: 0; border-width:0px;" v-on:input="getWidgetData(item)"
                        type="date"
                        placeholder="From"
                        :format="day_of_week + date_format_cal"
                        v-model="item.start_date"
                        :picker-options="{ firstDayOfWeek: first_day_of_week }"
                      >
                      </el-date-picker>
                    </base-input>
                    <base-input class="ml-2" v-if="!globalPeriodOn && item.period && item.period.id === 16"
                                style="flex: 1" label="End date">
                      <el-date-picker
                        style="border: 0; border-width:0px;" v-on:input="getWidgetData(item)"
                        type="date"
                        placeholder="To"
                        :format="day_of_week + date_format_cal"
                        v-model="item.end_date"
                        :picker-options="{ firstDayOfWeek: first_day_of_week }"
                      >
                      </el-date-picker>
                    </base-input>
                    <base-input class="ml-2" v-if="isCompareEligible(item)" label="Compare" style="max-width: 200px; flex: 1">
                      <el-select
                        class="select-primary mb-3 pagination-select"
                        v-model="item.compare_mode" v-on:change="getWidgetData(item)"
                        placeholder="Compare"
                      >
                        <el-option class="select-primary" label="None" value="none"/>
                        <el-option class="select-primary" label="Previous periods" value="previous_periods"/>
                        <el-option class="select-primary" label="Same period previous years" value="previous_years"/>
                      </el-select>
                    </base-input>
                    <base-input class="ml-2" v-if="isCompareEligible(item) && item.compare_mode !== 'none'" label="Periods" style="max-width: 90px">
                      <el-select
                        class="select-primary mb-3 pagination-select"
                        v-model="item.compare_count" v-on:change="getWidgetData(item)"
                        placeholder="Periods"
                      >
                        <el-option class="select-primary" v-for="c in [2,3,4,5,6,7,8,9,10]" :key="'CC' + c" :label="c" :value="c"/>
                      </el-select>
                    </base-input>
                    <base-input class="ml-2" v-if="isGranularityEligible(item)" label="Group by" style="max-width: 130px; flex: 1">
                      <el-select
                        class="select-primary mb-3 pagination-select"
                        v-model="item.granularity" v-on:change="getWidgetData(item)"
                        placeholder="Group by"
                      >
                        <el-option class="select-primary" label="Automatic" value="auto"/>
                        <el-option class="select-primary" label="Daily" value="day"/>
                        <el-option class="select-primary" label="Weekly" value="week"/>
                        <el-option class="select-primary" label="Monthly" value="month"/>
                      </el-select>
                    </base-input>
                    <div v-if="item.allow_export && item.widget_type_id === 3" style="display: flex; align-items: center; margin-left: auto;">
                      <base-button
                        type="link"
                        size="sm"
                        @click="exportWidgetData(item, $event)"
                        style="padding: 0; min-width: 40px; min-height: 40px;"
                        title="Export data"
                      >
                        <i class="tim-icons icon-cloud-download-93 text-white"></i>
                      </base-button>
                    </div>
                  </div>
                </div>

                <!-- Export Button - Top Right (for Chart and Activity widgets only) -->
                <div v-if="item.allow_export && item.widget_type_id !== 3" style="position: absolute; top: 2px; right: 5px; z-index: 10;">
                  <base-button
                    type="link"
                    size="sm"
                    @click="exportWidgetData(item, $event)"
                    style="padding: 0; min-width: 40px; min-height: 40px;"
                    title="Export data"
                  >
                    <i class="tim-icons icon-cloud-download-93 text-white"></i>
                  </base-button>
                </div>

                <!-- Widget Content Area - grows to fill space -->
                <div class="widget-content-area">
                  <!-- Chart Widget -->
                  <div v-if="item.widget_type_id === 1" class="widget-chart-block">
                  <div class="row">
                    <div class="col">
                      <h4 style="margin-bottom: 0px">{{item.title}}
                        <el-tooltip v-if="isCurrencyScoped(item)" :content="currencyScopedNote(item)" effect="light" placement="top" popper-class="otc-pop"><i class="tim-icons icon-alert-circle-exc text-warning" style="font-size: 0.8em; cursor: pointer"></i></el-tooltip>
                        <el-tooltip v-if="unappliedGlobalFilters(item).length > 0" :content="unappliedGlobalFilters(item).map(f => f.label + ': ' + f.why).join('. ')" effect="light" placement="top" popper-class="otc-pop"><i class="tim-icons icon-alert-circle-exc text-muted" style="font-size: 0.8em; cursor: pointer"></i></el-tooltip></h4>
                      <p class="card-title">
                        {{item.label}}
                      </p>
                    </div>
                  </div>
                  <div v-if="item.data && item.data.length > 0" class="widget-chart-area">
                    <line-chart fill v-if="item.chart_type_id === 1" :chartData="item.data"
                                :comparison-series="getComparisonSeries(item)"
                                :granularity="item.applied_granularity"
                                :primary-label="item.comparison ? formatComparisonRange(item.calculated_start_date, item.calculated_end_date) : ''"/>
                    <bar-chart fill v-if="item.chart_type_id === 2 && item.group_by_field !== 'company_group'" :chartData="item.data" :data-format="(item.table_id === 18 || item.table_id === 19) ? 'count' : (item.y_axis_dimension ? item.y_axis_dimension.label : 'count')"
                               :comparison-series="getComparisonSeries(item)"
                               :primary-label="item.comparison ? formatComparisonRange(item.calculated_start_date, item.calculated_end_date) : ''"/>
                    <pie-chart fill v-if="item.chart_type_id === 3" :chartData="item.data" :data-format="(item.table_id === 18 || item.table_id === 19) ? 'count' : (item.y_axis_dimension ? item.y_axis_dimension.label : 'count')"/>
                    <utilization-widget v-if="item.chart_type_id === 4 && item.table_id === 21" :data="item.data" :grouped="!!item.group_by_user"/>
                    <gauge-chart fill :color="item.highlight_color" v-if="item.chart_type_id === 4 && item.table_id !== 21" :chartData="item.data" :caption="item.table_id === 25 ? 'visits converted' : 'slots booked'" :unit="item.table_id === 25 ? 'visits' : 'slots'"/>
                    <heatmap-chart fill :color="item.highlight_color" v-if="item.chart_type_id === 5" :chartData="item.data"/>
                    <funnel-chart fill :color="item.highlight_color" v-if="item.chart_type_id === 6" :chartData="item.data"/>
                    <group-comparison-chart fill :color="item.highlight_color" v-if="item.chart_type_id === 2 && item.group_by_field === 'company_group'" :chartData="item.data" :format="(v) => getAmount(item.data_type, v)"/>
                  </div>
                  <div v-else class="text-center text-muted mt-3">
                    <p>{{ noDataText(item) }}</p>
                  </div>
                </div>

                <!-- Activity Stream Widget -->
                <div v-if="item.widget_type_id === 2" class="widget-activity-block">
                  <div class="row">
                    <div class="col">
                      <h4 style="margin-bottom: 0px">{{item.title}}
                        <el-tooltip v-if="isCurrencyScoped(item)" :content="currencyScopedNote(item)" effect="light" placement="top" popper-class="otc-pop"><i class="tim-icons icon-alert-circle-exc text-warning" style="font-size: 0.8em; cursor: pointer"></i></el-tooltip>
                        <el-tooltip v-if="unappliedGlobalFilters(item).length > 0" :content="unappliedGlobalFilters(item).map(f => f.label + ': ' + f.why).join('. ')" effect="light" placement="top" popper-class="otc-pop"><i class="tim-icons icon-alert-circle-exc text-muted" style="font-size: 0.8em; cursor: pointer"></i></el-tooltip></h4>
                      <p class="card-title">
                        {{item.label}}
                      </p>
                    </div>
                  </div>
                  <div v-if="item.data && item.data.length > 0" class="widget-activity-list">
                    <div class="widget-activity-scroll">
                      <b-table :items="item.data"
                               :fields="activityFields(item)"
                               table-class="widget-activity-table">
                        <template v-slot:cell(action)="row">
                          <div v-fit-text :title="row.item.description">{{row.item.description}}</div>
                        </template>
                        <template v-slot:cell(booked_event)="row">
                          <div v-fit-text :title="activityEvent(row.item).label + (activityEvent(row.item).when ? ', ' + activityEvent(row.item).when : '')">
                            <a v-if="activityEvent(row.item).bookingSlotId && hasPermissions('bookings_view')"
                               :href="'/bookings?selected_booking=' + activityEvent(row.item).bookingSlotId"
                               target="_blank" title="Open booking">{{activityEvent(row.item).label}} <i class="tim-icons el-icon-top-right text-primary"></i></a>
                            <span v-else>{{activityEvent(row.item).label}}</span>
                            <span v-if="activityEvent(row.item).when" class="widget-activity-when"> {{activityEvent(row.item).when}}</span>
                            <a v-if="activityEvent(row.item).transactionId && hasPermissions('transactions_view')"
                               :href="'/purchases/transactions/details?id=' + activityEvent(row.item).transactionId"
                               target="_blank" title="Open transaction" class="ml-1"><i class="tim-icons icon-cart text-primary"></i></a>
                          </div>
                        </template>
                        <template v-slot:cell(transaction)="row">
                          <div v-fit-text>
                            <a v-if="activityEvent(row.item).transactionId && hasPermissions('transactions_view')"
                               :href="'/purchases/transactions/details?id=' + activityEvent(row.item).transactionId"
                               target="_blank">{{activityEvent(row.item).label}} <i class="tim-icons el-icon-top-right text-primary"></i></a>
                            <span v-else>{{activityEvent(row.item).label}}</span>
                          </div>
                        </template>
                        <template v-slot:cell(performed_by)="row">
                          <div v-fit-text>{{row.item.user || row.item.customer || 'System'}}</div>
                        </template>
                        <template v-slot:cell(time_of_event)="row">
                          <div v-fit-text>{{getLocalDateTime(row.item.timestamp)}}</div>
                        </template>
                      </b-table>
                    </div>
                  </div>
                  <div v-else class="text-center text-muted mt-3">
                    <p>No recent activity</p>
                  </div>
                </div>

                <!-- Statistic Widget -->
                <div v-if="item.widget_type_id === 3">
                  <div v-if="item.allow_export && !(item.show_range_option && !item.is_all_time)" class="row">
                    <div class="col text-right">
                      <base-button
                        type="link"
                        size="sm"
                        @click="exportWidgetData(item, $event)"
                        style="padding: 0; min-width: 40px; min-height: 40px;"
                        title="Export data"
                      >
                        <i class="tim-icons icon-cloud-download-93 text-white"></i>
                      </base-button>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col text-right">
                      <h4 style="margin: 0;" v-fit-text>{{item.title}}
                        <el-tooltip v-if="isCurrencyScoped(item)" :content="currencyScopedNote(item)" effect="light" placement="top" popper-class="otc-pop"><i class="tim-icons icon-alert-circle-exc text-warning" style="font-size: 0.8em; cursor: pointer"></i></el-tooltip>
                        <el-tooltip v-if="unappliedGlobalFilters(item).length > 0" :content="unappliedGlobalFilters(item).map(f => f.label + ': ' + f.why).join('. ')" effect="light" placement="top" popper-class="otc-pop"><i class="tim-icons icon-alert-circle-exc text-muted" style="font-size: 0.8em; cursor: pointer"></i></el-tooltip></h4>
                    </div>
                  </div>

                  <!-- Single Statistic (default) -->
                  <div v-if="item.data && item.data.length > 0 && item.data[0] && !item.group_by_game && !item.group_by_user && !item.group_by_field">
                    <div class="row">
                      <div class="col text-right">
                        <h1 class="card-title widget-stat-value" v-fit-text>
                          {{getAmount(item.data_type, item.data[0].statistic)}}</h1>
                      </div>
                    </div>
                    <div class="row" v-if="item.comparison && item.comparison.periods.length === 1">
                      <div class="col text-right">
                        <span v-if="getStatDelta(item, item.comparison.periods[0])"
                              :class="getStatDelta(item, item.comparison.periods[0]).up ? 'text-success' : 'text-danger'">
                          <i :class="getStatDelta(item, item.comparison.periods[0]).up ? 'tim-icons el-icon-caret-top' : 'tim-icons el-icon-caret-bottom'"></i>
                          {{ Math.abs(getStatDelta(item, item.comparison.periods[0]).pct) }}%
                        </span>
                        <span v-else class="text-muted">n/a</span>
                        <small class="text-muted ml-1">vs {{ formatComparisonRange(item.comparison.periods[0].start, item.comparison.periods[0].end) }}</small>
                      </div>
                    </div>
                    <div v-if="item.comparison && item.comparison.periods.length > 1" style="max-height: 400px; overflow-y: auto;">
                      <table class="table table-sm">
                        <tbody>
                          <tr v-for="(p, idx) in item.comparison.periods" :key="'cmp-' + idx" style="border-bottom: 1px solid rgba(125,125,125,0.2);">
                            <td class="text-left" style="padding: 8px 5px;">
                              <span style="font-size: 0.9rem;">{{ formatComparisonRange(p.start, p.end) }}</span>
                            </td>
                            <td class="text-right" style="padding: 8px 5px;">
                              <strong style="font-size: 1rem;" v-if="getComparisonStat(p) !== null">{{ getAmount(item.data_type, p.data[0].statistic) }}</strong>
                              <strong style="font-size: 1rem;" v-else class="text-muted">n/a</strong>
                            </td>
                            <td class="text-right" style="padding: 8px 5px; width: 70px;">
                              <span v-if="getStatDelta(item, p)"
                                    :class="getStatDelta(item, p).up ? 'text-success' : 'text-danger'"
                                    style="font-size: 0.85rem;">
                                {{ getStatDelta(item, p).up ? '+' : '' }}{{ getStatDelta(item, p).pct }}%
                              </span>
                              <span v-else class="text-muted" style="font-size: 0.85rem;">n/a</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <!-- Grouped Statistics (by game) -->
                  <div v-else-if="item.data && item.data.length > 0 && item.group_by_game" style="max-height: 400px; overflow-y: auto;">
                    <div class="mb-2">
                      <small class="text-white" style="opacity: 0.85">
                        <i class="tim-icons icon-app mr-1"></i>
                        Grouped by event ({{item.data.length}} events)
                      </small>
                    </div>
                    <table class="table table-sm">
                      <tbody>
                        <tr v-for="(stat, idx) in item.data" :key="'stat-' + idx" style="border-bottom: 1px solid rgba(125,125,125,0.2);">
                          <td class="text-left" style="padding: 10px 5px;">
                            <span style="font-size: 0.95rem;">{{stat.game_name}}</span>
                          </td>
                          <td class="text-right" style="padding: 10px 5px;">
                            <strong style="font-size: 1.1rem;">{{getAmount(item.data_type, stat.statistic)}}</strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Grouped Statistics (by staff member) -->
                  <div v-else-if="item.data && item.data.length > 0 && item.group_by_user" style="max-height: 400px; overflow-y: auto;">
                    <div class="mb-2">
                      <small class="text-white" style="opacity: 0.85">
                        <i class="tim-icons icon-single-02 mr-1"></i>
                        Grouped by staff member ({{item.data.length}} staff)
                      </small>
                    </div>
                    <table class="table table-sm">
                      <tbody>
                        <tr v-for="(stat, idx) in item.data" :key="'stat-user-' + idx" style="border-bottom: 1px solid rgba(125,125,125,0.2);">
                          <td class="text-left" style="padding: 10px 5px;">
                            <span style="font-size: 0.95rem;">{{stat.user_name}}</span>
                          </td>
                          <td class="text-right" style="padding: 10px 5px;">
                            <strong style="font-size: 1.1rem;">{{getAmount(item.data_type, stat.statistic)}}</strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Grouped statistics (by field: currency, or PM status / priority / assignee / category / user / project) -->
                  <div v-else-if="item.data && item.data.length > 0 && item.group_by_field" style="max-height: 400px; overflow-y: auto;">
                    <div class="mb-2">
                      <small class="text-white" style="opacity: 0.85">
                        <i class="tim-icons icon-coins mr-1" v-if="item.group_by_field === 'currency'"></i><i class="tim-icons icon-chart-bar-32 mr-1" v-else></i>
                        Grouped by {{item.group_by_field}} ({{item.data.length}})
                      </small>
                    </div>
                    <table class="table table-sm">
                      <tbody>
                        <tr v-for="(stat, idx) in item.data" :key="'stat-field-' + idx" style="border-bottom: 1px solid rgba(125,125,125,0.2);">
                          <td class="text-left" style="padding: 10px 5px;">
                            <span style="font-size: 0.95rem;">{{stat.label}}</span>
                          </td>
                          <td class="text-right" style="padding: 10px 5px;">
                            <strong style="font-size: 1.1rem;">{{ stat.currency ? formatCurrencyIn(stat.currency, stat.statistic) : getAmount(item.data_type, stat.statistic) }}</strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div v-else class="text-center text-muted mt-3">
                    <p>{{ noDataText(item) }}</p>
                  </div>
                  <div class="row">
                    <div class="col text-right">
                      <p class="card-title widget-stat-label" v-fit-text>
                        {{item.label}}
                      </p>
                    </div>
                  </div>
                </div>
                </div>

                <!-- Widget Description - Always at bottom -->
                <div v-if="item.show_description_label !== false" class="widget-desc-footer" :title="getWidgetDescription(item)">
                  <small v-fit-text="{ short: shortenWidgetDescription(getWidgetDescription(item)) }">{{getWidgetDescription(item)}}</small>
                </div>
              </div>
            </card>
          </grid-item>
        </grid-layout>
      </div>
    </div>

    <!-- Export Widget Data Modal -->
    <modal :show.sync="modals.export"
           body-classes="p-0"
           modal-classes="modal-dialog-centered modal-sm">
      <card type="secondary"
            header-classes="bg-white pb-5"
            body-classes="px-lg-5 py-lg-5"
            class="border-0 mb-0">

        <h4 class="card-title">Export widget data</h4>

        <div class="row">
          <div class="col">
            <base-radio v-model="exportFormat" name="csv" class="mb-3">
              CSV (Comma Separated Values)
            </base-radio>
            <base-radio v-model="exportFormat" name="json" class="mb-3">
              JSON (JavaScript Object Notation)
            </base-radio>
            <base-radio v-model="exportFormat" name="png" class="mb-3">
              PNG (Image)
            </base-radio>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col text-center">
            <base-button type="secondary" @click="modals.export = false">Cancel</base-button>
            <base-button class="ml-3" type="primary" @click="performExport()">Export</base-button>
          </div>
        </div>

      </card>
    </modal>

  </div>
</template>

<script>
import {Auth} from "aws-amplify";
import {API_LOCATION} from "@/api-config";
import axios from "axios";
import {Dropdown, DropdownMenu} from "element-ui";
import VueGridLayout from 'vue-grid-layout';
import PieChart from "@/components/Charts/PieChart.vue";
import BarChart from "@/components/Charts/BarChart.vue";
import LineChart from "@/components/Charts/LineChart.vue";
import HeatmapChart from "@/components/Charts/HeatmapChart.vue";
import FunnelChart from "@/components/Charts/FunnelChart.vue";
import GaugeChart from "@/components/Charts/GaugeChart.vue";
import GroupComparisonChart from "@/components/Charts/GroupComparisonChart.vue";
import { shortenWidgetDescription } from "@/util/widgetDescription";
import { describeActivityEvent } from "@/util/activityStreamEvent";
import { viewportColumns, repackLayout } from "@/util/responsiveGridLayout";
import UtilizationWidget from "@/components/Charts/UtilizationWidget.vue";
import {date, formatGroupDate, formatGroupTime} from "@/plugins/dates";
import moment from 'moment'
import {BTable} from "bootstrap-vue";
import _ from "lodash";
import currencies from "@/assets/currencies.json";
import html2canvas from 'html2canvas';
import { getPaymentMethods as fetchPaymentMethods } from '@/services/groupData'
import { emptyConfig, normalizeConfig, buildFilterSchema, buildWidgetQuery, activeLabelParts, getMethodKey, globalPeriodActive, FILTER_LABELS, MIN_REFRESH_MINUTES, emailTemplateOptions, emailTemplateTypeOptions } from './dashboardGlobalFilters'

export default {
  name: "DashboardDisplay",
  components: {
    HeatmapChart,
    FunnelChart,
    GaugeChart,
    GroupComparisonChart,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    PieChart,
    BarChart,
    LineChart,
    UtilizationWidget,
    BTable,
    GridLayout: VueGridLayout.GridLayout,
    GridItem: VueGridLayout.GridItem,
  },
  data() {
    return {
      allGroups: [],
      filterBarConfig: emptyConfig(),
      filterValues: {},
      filterOptions: null,
      paymentMethodOptions: [],
      filterBarKey: 0,
      filtersLoading: false,
      refreshTimer: null,
      viewportCols: 0,   // 0 = stored layout; 1 or 2 = repacked for narrow viewports
      loading: false,
      fullscreen: false,
      dashboard: '',
      dashboards: [],
      noDashboards: false,
      layout: [],
      widgetLoadingStates: {}, // Track loading state per widget
      day_of_week: '',
      date_format_cal: '',
      measurements: [{label:'count', name: 'Count'}, {label: 'amount', name: 'Amount paid'}, {label: 'refunded', name: 'Amount refunded'}],
      purchaseMeasurements: [{label:'count', name: 'Number of sales'}, {label: 'quantity', name: 'Units sold'}, {label: 'amount', name: 'Revenue (total)'}, {label: 'price', name: 'Subtotal'}, {label: 'tax', name: 'Tax'}, {label: 'fee', name: 'Fee'}, {label: 'discount', name: 'Discount'}],
      date_format: JSON.parse(localStorage.getItem('group')).date_format,
      first_day_of_week: JSON.parse(localStorage.getItem('group')).first_day_of_week,
      currency_symbol: '$',
      currency: JSON.parse(localStorage.getItem('group')).currency,
      currency_format: JSON.parse(localStorage.getItem('group')).currency_format,
      number_format: JSON.parse(localStorage.getItem('group')).number_format,
      currencies: currencies.currencies,
      j: 0,
      time_period_types: [],
      modals: { export: false },
      exportFormat: 'csv',
      exportWidget: null,
    }
  },
  watch: {
    '$route.query': {
      handler(newQuery, oldQuery) {
        // Skip if this is the initial call (oldQuery will be undefined)
        if (oldQuery === undefined) {
          return
        }

        if (Object.keys(newQuery).length === 0) {
          this.getMainDashboard()
        }
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    gridLayout(){
      return this.viewportCols === 0 ? this.layout : repackLayout(this.layout, this.viewportCols)
    },
    filterSchema(){
      if(!this.filterOptions) return []
      return buildFilterSchema(this.filterBarConfig.keys, this.filterValues, { ...this.filterOptions, timePeriodTypes: this.time_period_types, paymentMethods: this.paymentMethodOptions })
    },
    globalPeriodOn(){
      return this.filterBarConfig.enabled && globalPeriodActive(this.filterValues)
    },
    gridCols(){
      // Always 12: legacy 6-column dashboards are doubled in memory at load, so the grid never
      // renders at 6. A transient 6-column frame before the dashboard loaded made vue-grid-layout
      // clamp and compact 12-column layouts (and the settings page then persisted the damage).
      return 12
    },
  },
  methods: {
    isCurrencyScoped(item){
      // A company-scope money widget pinned to a same-currency subset while other groups
      // use different currencies (the auto-scoped template case, or a manual selection).
      if(!item || item.scope !== 'company' || item.data_type !== 'currency') return false
      if(item.group_by_field === 'currency') return false
      const ids = item.company_group_ids
      if(!Array.isArray(ids) || ids.length === 0 || this.allGroups.length === 0) return false
      const selected = new Set(ids)
      const currencies = new Set(this.allGroups.filter(g => selected.has(g.id)).map(g => g.currency))
      if(currencies.size !== 1) return false
      const currency = [...currencies][0]
      return this.allGroups.some(g => !selected.has(g.id) && g.currency !== currency)
    },
    currencyScopedNote(item){
      const n = Array.isArray(item.company_group_ids) ? item.company_group_ids.length : 0
      return 'Limited to ' + n + (n === 1 ? ' group' : ' groups') + ' sharing one currency because your groups use different currencies. Edit the widget to change the selection.'
    },
    async getPaymentMethods(){
      try {
        const data = await fetchPaymentMethods()
        if(data.payment_methods && data.payment_methods.length > 0){
          this.paymentMethodOptions = data.payment_methods.map(pm => ({ value: getMethodKey(pm.name), label: pm.name }))
        }
      } catch(err) {
        console.log(err)
      }
    },
    scheduleAutoRefresh(){
      // Reload widget data only (not metadata), so global filter values and per-tile period
      // choices survive each tick. Legacy intervals below the floor are raised to it.
      if(this.refreshTimer){ clearInterval(this.refreshTimer); this.refreshTimer = null }
      if(!this.dashboard || !Number(this.dashboard.refresh_enabled)) return
      const minutes = Math.max(MIN_REFRESH_MINUTES, Number(this.dashboard.refresh_interval_minutes) || MIN_REFRESH_MINUTES)
      this.refreshTimer = setInterval(() => {
        if(document.hidden) return   // don't hammer the API from a background tab
        this.loadAllWidgets()
      }, minutes * 60000)
    },
    async loadAllWidgets(){
      // Section widgets (type 4) have no data. Up to 8 requests run at once; a widget is only
      // dequeued while the dashboard captured up front is still the one on screen, so after a
      // switch nothing queued here is requested under the new id (a 401).
      const dashboardId = this.dashboard.id
      const queue = this.layout.filter(w => w.widget_type_id !== 4)
      const worker = async () => {
        while (queue.length > 0) {
          if(this.dashboard.id !== dashboardId) return
          const w = queue.shift()
          await this.loadWidgetData(w, false)
        }
      }
      await Promise.all(Array.from({ length: Math.min(8, queue.length) }, worker))
    },
    async onGlobalFilterChange(values){
      this.filterValues = values
      this.filtersLoading = true
      await this.loadAllWidgets()
      this.filtersLoading = false
    },
    async onGlobalFilterQuickAction(key){
      if(key !== 'reset') return
      this.filterValues = JSON.parse(JSON.stringify(this.filterBarConfig.defaults))
      this.filterBarKey++          // remounts FilterBar so it re-reads initial-value
      await this.onGlobalFilterChange(this.filterValues)
    },
    globalFilterLine(item){
      // One entry per active global filter: applied or not, so no tile is ambiguous.
      if(!this.filterBarConfig.enabled || item.widget_type_id === 4) return []
      const parts = activeLabelParts(this.filterValues, this.filterBarConfig.keys, this.filterSchema)
      const state = item.filter_state || {}
      const stateKey = { period: 'date', location: 'location', vertical: 'event', genre: 'event', event_name: 'event', event: 'event', staff: 'staff', payment_method: 'payment_method', purchase_type: 'purchase_type', promo: 'promo', email_template: 'email_template', email_template_type: 'email_template_type', traffic: 'traffic' }
      return parts.map(p => {
        const st = state[stateKey[p.key]] || 'applied'
        const why = st === 'all_time' ? 'This widget shows all time and ignores the global date'
          : st === 'not_applicable' ? 'This widget\'s data source has no ' + FILTER_LABELS[p.key].toLowerCase() + ' dimension'
          : st === 'excluded' ? 'This widget is scoped to this group, which is not among the selected locations'
          : st === 'currency_conflict' ? 'The selected locations use different currencies, so this money widget cannot combine them'
          : ''
        return { key: p.key, label: p.label, applied: st === 'applied', why }
      })
    },
    unappliedGlobalFilters(item){
      return this.globalFilterLine(item).filter(f => !f.applied)
    },
    noDataText(item){
      const st = item.filter_state || {}
      if(st.location === 'excluded') return 'No data for the selected locations'
      if(st.location === 'currency_conflict') return 'Selected locations use different currencies'
      if(this.globalFilterLine(item).length > 0) return 'No data for the selected filters'
      return 'No data available'
    },
    async getGroups(){
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      try {
        let response = await this.axios.get(API_LOCATION + 'companies/' + companyId + '/groups', config)
        this.allGroups = response.data
      } catch (err) {
        console.log(err)
      }
    },
    onWidgetResized(){
      // Tell fill-mode charts to re-read their container; chart.js's own container
      // detection is unreliable inside transformed grid items.
      window.dispatchEvent(new Event('dashboard-widget-resize'))
    },
    activityFields(item){
      // Booking activity rows carry the booked event; transaction activity rows carry the order
      // number. Other sources keep the three original columns.
      // Fixed column widths (table-layout: fixed) so every cell has a bounded width for
      // v-fit-text to shrink into; the action column takes whatever remains.
      const col = (key, label, width) => ({ key, label, thStyle: width ? { width } : undefined })
      const time = col('time_of_event', 'Time', '19%')
      const action = col('action', 'Action')
      const by = col('performed_by', 'Performed by', '16%')
      const source = Number(item.activity_source_id)
      if(source === 2) return [time, col('booked_event', 'Booked event', '26%'), action, by]
      if(source === 3) return [time, col('transaction', 'Transaction', '17%'), action, by]
      return [time, action, by]
    },
    activityEvent(row){
      let group = null
      try { group = JSON.parse(localStorage.getItem('group')) } catch(e) { group = null }
      return describeActivityEvent(row, group)
    },
    hasPermissions(scope){
      let permissions = JSON.parse(localStorage.getItem('permissions'))
      const matchFound = permissions.some(element => element === scope);
      return matchFound
    },
    getCSS(){
      if(this.fullscreen){
        return {
          'overflow-x': 'hidden',
          'padding' : '30px 20px 20px 20px',
        }
      }
      return {}
    },
    getLocalDateTime(d){
      if(!d) return ''
      const date = formatGroupDate(d, { dayOfWeek: false })
      const time = formatGroupTime(d)
      if(!date && !time) return ''
      return `${date}, ${time}`
    },
    getAmount(data_format, integer) {
      if (integer == null) {
        // A sum or count over zero rows comes back as NULL; that is a zero, not "no value".
        // Dates have no meaningful zero, so they stay blank.
        if (data_format === 'date') return '';
        integer = 0;
      }

      if (data_format === 'currency') {
        return this.formatCurrency(integer);
      }

      if (data_format === 'date') {
        return date(integer, this.date_format);
      }

      if (data_format === 'percent') {
        return integer + '%';
      }

      if (data_format === 'hours') {
        return integer + 'h';
      }

      return integer;
    },
    formatCurrencyIn(code, amount) {
      // Same rules as formatCurrency, for a row that carries its own currency code.
      if (!code || code === this.currency) return this.formatCurrency(amount);
      if (amount == null || amount === '') return '';
      const value = parseFloat(amount);
      if (isNaN(value)) return amount;
      if (code === 'SEK' || code === 'DKK') {
        return `${value.toLocaleString(code === 'DKK' ? 'da-DK' : 'sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${code === 'DKK' ? 'kr.' : 'kr'}`;
      }
      const match = _.find(this.currencies, { code });
      const symbol = match ? match.symbol : code + ' ';
      if (this.number_format === 1) {
        const localizedAmount = value.toLocaleString(undefined, { minimumFractionDigits: 2 }).replace('.', ',');
        return this.currency_format === 0 ? symbol + localizedAmount : localizedAmount + ' ' + code;
      }
      return this.currency_format === 0 ? symbol + value.toFixed(2) : value.toFixed(2) + ' ' + code;
    },
    formatCurrency(amount) {
      if (amount == null || amount === '') return '';

      const value = parseFloat(amount);
      if (isNaN(value)) return amount;

      // Handle SEK and DKK formatting (postfix krone symbols)
      if (this.currency === 'SEK' || this.currency === 'DKK') {
        const formattedAmount = value.toLocaleString(this.currency === 'DKK' ? 'da-DK' : 'sv-SE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
        return `${formattedAmount} ${this.currency === 'DKK' ? 'kr.' : 'kr'}`;
      }

      // Handle other currency formatting based on existing logic
      if (this.number_format === 1) {
        const localizedAmount = value.toLocaleString(undefined, {
          minimumFractionDigits: 2
        }).replace('.', ',');

        if (this.currency_format === 0) {
          return this.currency_symbol + localizedAmount;
        } else {
          return localizedAmount + ' ' + this.currency;
        }
      }

      // Default formatting
      if (this.currency_format === 0) {
        return this.currency_symbol + value.toFixed(2);
      } else {
        return value.toFixed(2) + ' ' + this.currency;
      }
    },
    formatDateRange(startDate, endDate) {
      if (!startDate || !endDate) return null;
      const start = moment(startDate, 'YYYY-MM-DD HH:mm:ss');
      const end = moment(endDate, 'YYYY-MM-DD HH:mm:ss');
      if (!start.isValid() || !end.isValid()) return null;

      const sameYear = start.year() === end.year();
      const sameMonth = start.month() === end.month() && sameYear;

      let startFormat, endFormat;

      if (sameMonth) {
        // Same month and year: "Dec 5 - 11"
        startFormat = start.format('MMM D');
        endFormat = end.format('D');
      } else if (sameYear) {
        // Same year, different months: "Dec 5 - Jan 11"
        startFormat = start.format('MMM D');
        endFormat = end.format('MMM D');
      } else {
        // Different years: "Dec 28, 2024 - Jan 3, 2025"
        startFormat = start.format('MMM D, YYYY');
        endFormat = end.format('MMM D, YYYY');
      }

      return `${startFormat} - ${endFormat}`;
    },
    getWidgetDescription(item) {
      // Activity streams have no metric; describe the source and the window instead of
      // whatever metric fields the row happens to carry.
      if (item.widget_type_id === 2) {
        const sources = { 1: 'Event log', 2: 'Booking activity', 3: 'Transaction activity', 4: 'Customer emails' }
        const source = sources[Number(item.activity_source_id)] || 'Activity'
        return `${source} • Latest ${item.event_limit || 10} • Last ${item.activity_window_days || 90} days`
      }
      const parts = [];

      // Data source first
      const dataSources = {
        1: 'Bookings',
        3: 'Games',
        8: 'Checklists',
        13: 'Waivers',
        15: 'Payments',
        17: 'Purchases'
      };
      if (item.table_id && dataSources[item.table_id]) {
        parts.push(dataSources[item.table_id]);
      }

      // Purchases bar/pie widgets are driven by the measurement dropdown, not the
      // metric type, so the description shows the measurement name (e.g. "Number of
      // sales", "Units sold", "Revenue (total)") instead of the generic metric text.
      if (item.table_id === 17 && (item.chart_type_id === 2 || item.chart_type_id === 3)
        && item.y_axis_dimension && item.y_axis_dimension.name) {
        parts.push(item.y_axis_dimension.name);
      }
      // Metric type + field name (combined without dot)
      else if (item.metric_type_id) {
        let metricText = '';
        const fieldName = item.field_name ? item.field_name.toLowerCase() : '';

        if (item.metric_type_id === 1) {
          metricText = 'Total count';
        } else if (item.metric_type_id === 2) {
          metricText = fieldName ? `Sum of ${fieldName}` : 'Sum of';
        } else if (item.metric_type_id === 3) {
          metricText = fieldName ? `Highest ${fieldName} value` : 'Highest value';
        } else if (item.metric_type_id === 4) {
          metricText = fieldName ? `Lowest ${fieldName} value` : 'Lowest value';
        } else if (item.metric_type_id === 5) {
          metricText = fieldName ? `Average of ${fieldName}` : 'Average of';
        }

        if (metricText) {
          parts.push(metricText);
        }
      }

      // Game filter - show prominently if filtered
      if (item.game_name && !item.group_by_game) {
        parts.push(`Filtered by ${item.game_name}`);
      }
      if (item.group_by_game) {
        parts.push('Grouped by event');
      }

      // Staff member filter - show if filtered
      if (item.company_user_name && !item.group_by_user) {
        parts.push(`Filtered by ${item.company_user_name}`);
      }
      if (item.group_by_user) {
        parts.push('Grouped by staff member');
      }
      if (item.group_by_field === 'currency') {
        parts.push('Grouped by currency');
      }

      // Transactions data source: cancelled transactions are excluded unless the widget opts in
      if (item.table_id === 2 && item.purchase_item_filters) {
        try {
          const pf = typeof item.purchase_item_filters === 'string'
            ? JSON.parse(item.purchase_item_filters)
            : item.purchase_item_filters;
          if (pf && pf.include_cancelled_transactions) {
            parts.push('Including cancelled');
          }
        } catch (e) {
          // ignore malformed filter json in description
        }
      }

      // Promotion filter
      if (item.purchase_item_filters) {
        try {
          const pf = typeof item.purchase_item_filters === 'string'
            ? JSON.parse(item.purchase_item_filters)
            : item.purchase_item_filters;
          if (pf && Array.isArray(pf.promos) && pf.promos.length > 0) {
            const labels = pf.promos.map(ref => {
              const match = ((this.filterOptions && this.filterOptions.promos) || []).find(o => o.value === ref);
              return match ? match.label : ref;
            });
            parts.push(labels.length > 2 ? `${labels.length} promotions` : `Promotion: ${labels.join(', ')}`);
          }
        } catch (e) {
          // ignore malformed filter json in description
        }
      }

      // Email template and template type filters
      if (item.purchase_item_filters) {
        try {
          const pf = typeof item.purchase_item_filters === 'string'
            ? JSON.parse(item.purchase_item_filters)
            : item.purchase_item_filters;
          if (pf && Array.isArray(pf.email_template_ids) && pf.email_template_ids.length > 0) {
            const labels = pf.email_template_ids.map(id => {
              const match = emailTemplateOptions(this.filterOptions || {}).find(o => Number(o.value) === Number(id));
              return match ? match.label : String(id);
            });
            parts.push(labels.length > 2 ? `${labels.length} templates` : `Template: ${labels.join(', ')}`);
          }
          if (pf && Array.isArray(pf.email_template_type_ids) && pf.email_template_type_ids.length > 0) {
            const labels = pf.email_template_type_ids.map(id => {
              const match = emailTemplateTypeOptions(this.filterOptions || {}).find(o => Number(o.value) === Number(id));
              return match ? match.label : String(id);
            });
            parts.push(labels.length > 2 ? `${labels.length} template types` : `Template type: ${labels.join(', ')}`);
          }
        } catch (e) {
          // ignore malformed filter json in description
        }
      }

      // Time period with actual date range
      if (!item.is_all_time) {
        let periodText = '';
        if (item.period && (item.period.name || item.period.label)) {
          periodText = item.period.name || item.period.label;
        }

        // Add calculated date range if available
        const dateRange = this.formatDateRange(item.calculated_start_date, item.calculated_end_date);
        if (dateRange) {
          periodText = periodText ? `${periodText} (${dateRange})` : dateRange;
        }

        if (periodText) {
          parts.push(periodText);
        }
      } else {
        parts.push('All time');
      }

      if (item.compare_mode === 'previous_periods' && item.comparison) {
        parts.push('Compared to ' + (item.compare_count - 1) + ' previous period' + (item.compare_count > 2 ? 's' : ''));
      } else if (item.compare_mode === 'previous_years' && item.comparison) {
        parts.push('Compared to ' + (item.compare_count - 1) + ' previous year' + (item.compare_count > 2 ? 's' : ''));
      }

      if (item.scope === 'company') {
        const n = Array.isArray(item.company_group_ids) ? item.company_group_ids.length : 0;
        const access = item.scope_access
        if(access && access.excluded > 0){
          // The viewer's group access narrowed the widget: say so instead of pretending it is the full set.
          parts.push(access.visible + ' of ' + access.configured + ' groups (limited by your group access)');
        } else {
          parts.push(n > 0 ? n + (n === 1 ? ' group' : ' groups') : 'All groups');
        }
      }

      const gfl = this.globalFilterLine(item)
      if (gfl.some(f => f.applied)) {
        parts.push(gfl.some(f => !f.applied) ? 'Global filters partly applied' : 'Global filters applied');
      }

      const result = parts.join(' • ');
      return result;
    },
    formatComparisonRange(startDate, endDate){
      // Comparison labels always carry the year; formatDateRange omits it for
      // same-year ranges, which makes multi-year overlays indistinguishable.
      const label = this.formatDateRange(startDate, endDate)
      if(!label) return label
      const start = moment(startDate, 'YYYY-MM-DD HH:mm:ss')
      const end = moment(endDate, 'YYYY-MM-DD HH:mm:ss')
      if(start.year() === end.year()){
        return label + ', ' + start.year()
      }
      return label
    },
    isGranularityEligible(item){
      return item.widget_type_id === 1 && item.chart_type_id === 1 && item.show_range_option && !item.is_all_time
    },
    isCompareEligible(item){
      if(!item.show_range_option || item.is_all_time) return false
      if(item.show_compare_option === false) return false
      if(!item.period || item.period.period_key === 'last_24_hours') return false
      if(item.widget_type_id === 3){
        return !item.group_by_game && !item.group_by_user && !item.group_by_field
      }
      return item.widget_type_id === 1 && (item.chart_type_id === 1 || item.chart_type_id === 2)
    },
    getComparisonSeries(item){
      if(!item.comparison || !item.comparison.periods) return []
      return item.comparison.periods.map(p => ({
        label: this.formatComparisonRange(p.start, p.end),
        data: p.data || []
      }))
    },
    getComparisonStat(period){
      if(!period || !period.data || !period.data[0]) return null
      const v = period.data[0].statistic
      return v === null || v === undefined ? null : Number(v)
    },
    getPercentChange(current, prior){
      if(prior === null || prior === 0 || current === null || isNaN(current)) return null
      return Math.round(((current - prior) / Math.abs(prior)) * 1000) / 10
    },
    getStatDelta(item, period){
      const current = item.data && item.data[0] && item.data[0].statistic !== null && item.data[0].statistic !== undefined
        ? Number(item.data[0].statistic) : null
      const pct = this.getPercentChange(current, this.getComparisonStat(period))
      if(pct === null) return null
      return { pct: pct, up: pct >= 0 }
    },
    goToDashboardSettings(){
      this.$router.push('/dashboards/settings')
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    getMainDashboardId(url, config) {
      return new Promise ( function(resolve) {
        axios.get(url, config)
          .then(response => {
            resolve(response.data)
          })
          .catch(err => {
            this.loading = false
            console.log(err)
          })
      })
    },
    goToNormalScreen(dashboardId){
      this.$router.push({ path: '/dashboards/', query: { id: dashboardId }})
    },
    goToFullScreen(dashboardId){
      this.$router.push({ path: '/dashboards/fullscreen', query: { id: dashboardId }})
    },
    shortenWidgetDescription,
    onViewportChange(){
      const next = viewportColumns(window.innerWidth)
      if(next !== this.viewportCols){
        this.viewportCols = next
        this.$nextTick(() => this.onWidgetResized())
      }
    },
    async loadWidgetData(item, updatePeriod = false) {
      if(item.widget_type_id === 4) return   // sections carry no data
      // Prevent duplicate calls - check if already loading
      if (this.widgetLoadingStates[item.id]) {
        return
      }

      // Capture dashboard ID IMMEDIATELY before any async operations
      // This prevents race conditions when user switches dashboards quickly
      const dashboardId = this.dashboard.id

      // Set loading state for this widget
      this.$set(this.widgetLoadingStates, item.id, true)

      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      const tz = moment().utcOffset();
      const gq = this.filterBarConfig.enabled ? buildWidgetQuery(this.filterValues, item, this.time_period_types) : { dateParams: '', filtersParam: '' }
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId
        + '/dashboards/' + dashboardId + '/widgets/' + item.id + '?tz=' + tz
      url += gq.dateParams !== '' ? gq.dateParams
        : ('&time_period_type=' + item.period.period_key + '&time_period_type_id=' + item.period.id
          + '&start_date=' + moment(item.start_date, 'YYYY-MM-DD').format('YYYY-MM-DD')
          + '&end_date=' + moment(item.end_date, 'YYYY-MM-DD').format('YYYY-MM-DD'))
      url += gq.filtersParam

      if(item.compare_mode && item.compare_mode !== 'none' && this.isCompareEligible(item)){
        url += '&compare_mode=' + item.compare_mode + '&compare_count=' + item.compare_count
      }

      if(this.isGranularityEligible(item) && item.granularity){
        url += '&granularity=' + item.granularity
      }

      try {
        const response = await this.axios.get(url, config)

        // Check if dashboard changed while loading - discard stale responses
        if (this.dashboard.id !== dashboardId) {
          return
        }

        // Find the widget in layout and update it
        const layoutIndex = this.layout.findIndex(w => w.id === response.data.widget.id)
        if (layoutIndex !== -1) {
          // Use Vue.set to ensure reactivity
          this.$set(this.layout[layoutIndex], 'data', response.data.widget.data)
          this.$set(this.layout[layoutIndex], 'data_type', response.data.widget.data_type)
          // Activity streams: the data lambda clamps event_limit and reports the window it applied.
          if (response.data.widget.activity_source_id != null) this.$set(this.layout[layoutIndex], 'activity_source_id', Number(response.data.widget.activity_source_id))
          if (response.data.widget.event_limit != null) this.$set(this.layout[layoutIndex], 'event_limit', response.data.widget.event_limit)
          if (response.data.widget.activity_window_days != null) this.$set(this.layout[layoutIndex], 'activity_window_days', response.data.widget.activity_window_days)
          this.$set(this.layout[layoutIndex], 'scope_access', response.data.widget.scope_access || null)
          this.$set(this.layout[layoutIndex], 'game_id', response.data.widget.game_id || null)
          this.$set(this.layout[layoutIndex], 'game_name', response.data.widget.game_name || null)
          this.$set(this.layout[layoutIndex], 'group_by_game', Boolean(response.data.widget.group_by_game))
          this.$set(this.layout[layoutIndex], 'company_user_id', response.data.widget.company_user_id || null)
          this.$set(this.layout[layoutIndex], 'group_by_user', Boolean(response.data.widget.group_by_user))
          this.$set(this.layout[layoutIndex], 'group_by_field', response.data.widget.group_by_field || null)
          this.$set(this.layout[layoutIndex], 'company_user_name', response.data.widget.company_user_name || null)
          this.$set(this.layout[layoutIndex], 'comparison', response.data.comparison || null)
          this.$set(this.layout[layoutIndex], 'applied_granularity', response.data.granularity || 'day')
          this.$set(this.layout[layoutIndex], 'field_name', response.data.widget.field_name || null)
          this.$set(this.layout[layoutIndex], 'show_description_label', response.data.widget.show_description_label !== undefined ? Boolean(response.data.widget.show_description_label) : true)
          this.$set(this.layout[layoutIndex], 'allow_export', response.data.widget.allow_export !== undefined ? Boolean(response.data.widget.allow_export) : true)

          // Store calculated date range from backend
          this.$set(this.layout[layoutIndex], 'calculated_start_date', response.data.calculated_start_date || null)
          this.$set(this.layout[layoutIndex], 'calculated_end_date', response.data.calculated_end_date || null)
          this.$set(this.layout[layoutIndex], 'filter_state', response.data.widget.filter_state || null)

          if(updatePeriod) {
            this.$set(this.layout[layoutIndex], 'start_date', item.start_date)
            this.$set(this.layout[layoutIndex], 'end_date', item.end_date)
            this.$set(this.layout[layoutIndex], 'h', item.h)
            this.$set(this.layout[layoutIndex], 'y', item.y)
            this.$set(this.layout[layoutIndex], 'is_all_time', Boolean(response.data.widget.is_all_time))
            this.$set(this.layout[layoutIndex], 'period', _.find(this.time_period_types, {id: response.data.widget.time_period_type_id}))
          }
        }
      } catch(err) {
        console.log(err)
        if (err.response && err.response.status == 401) {
          this.$router.push('/forbidden')
        } else if(err.response && err.response.status == 400){
          this.$notify({ type: 'danger', message: typeof err.response.data === 'string' ? err.response.data : 'Something went wrong', icon: 'tim-icons icon-alert-circle-exc' })
        } else {
          this.$notify({ type: 'danger', message: 'Something went wrong loading widget data', icon: 'tim-icons icon-alert-circle-exc' })
        }
      } finally {
        this.$set(this.widgetLoadingStates, item.id, false)
      }
    },
    async getWidgetData(item){
      await this.loadWidgetData(item, true)
    },
    async getMainDashboard(dashboardId, event, polling, no_reload){
      if(event){
        event.preventDefault()
      }
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      const tz = moment().utcOffset();
      if(!polling && !no_reload){
        this.loading = true
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/dashboards/pinned' + '?tz=' + tz
        let dashboard = await this.getMainDashboardId(url, config)

        this.dashboards = dashboard.dashboards
        if(!dashboardId && dashboard.id === null){
          // Every dashboard in this group is restricted and none is shared with this user. Stop the
          // previous dashboard's auto refresh and drop its widgets, or the next tick would request
          // widgets this user can no longer open and the 401 handler would send them to /forbidden.
          if(this.refreshTimer){ clearInterval(this.refreshTimer); this.refreshTimer = null }
          this.layout = []
          this.noDashboards = true
          this.loading = false
          return
        }
        this.noDashboards = false
        if(!dashboardId ){
          dashboardId = dashboard.id
        }
      }

      if(no_reload){
        dashboardId = this.dashboard.id
      }

      // Load dashboard metadata only (no widget data)
      let url2 = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/dashboards/' + dashboardId + '?tz=' + tz + '&metadata_only=true'

      try {
        // Payment methods are only needed when the filter bar uses them, but the metadata
        // response decides that; start the cached read now so it is not a trailing round trip.
        const paymentMethodsP = this.paymentMethodOptions.length === 0 ? this.getPaymentMethods() : Promise.resolve()
        const response = await this.axios.get(url2, config)
        this.loading = false
        this.dashboard = response.data.dashboard
        // Legacy 6-column dashboards render doubled at 12 columns (visually identical);
        // deterministic on every load, so no persistence is needed from this read-only view.
        const legacyGrid = Number(this.dashboard.grid_cols) !== 12
        if(legacyGrid){
          this.$set(this.dashboard, 'grid_cols', 12)
        }
        this.time_period_types = response.data.time_period_types
        this.filterBarConfig = normalizeConfig(this.dashboard.filter_bar)
        this.filterValues = JSON.parse(JSON.stringify(this.filterBarConfig.defaults))
        this.filterOptions = response.data.filter_options || null
        this.filterBarKey++
        this.scheduleAutoRefresh()
        if(this.filterBarConfig.enabled && this.filterBarConfig.keys.includes('payment_method')){
          await paymentMethodsP
        }
        if(this.allGroups.length === 0 && response.data.widgets && Object.values(response.data.widgets).some(w => w.scope === 'company')){
          this.getGroups()
        }

        // Build layout with empty data
        this.layout = []

        for(let n in response.data.widgets){
          let time_period_type_id = response.data.widgets[n].time_period_type_id
          let start_date = response.data.widgets[n].start_date ? moment(response.data.widgets[n].start_date, "YYYY-MM-DD") : moment()
          let end_date = response.data.widgets[n].end_date ? moment(response.data.widgets[n].end_date, "YYYY-MM-DD") : moment()

          const widgetItem = {
            x: legacyGrid ? response.data.widgets[n].x * 2 : response.data.widgets[n].x,
            y: response.data.widgets[n].y,
            w: legacyGrid ? response.data.widgets[n].w * 2 : response.data.widgets[n].w,
            h: response.data.widgets[n].h,
            i: response.data.widgets[n].id,
            id: response.data.widgets[n].id,
            data: [], // Empty data initially
            label: response.data.widgets[n].label,
            title: response.data.widgets[n].title,
            highlight_color: response.data.widgets[n].highlight_color,
            widget_type_id: response.data.widgets[n].widget_type_id,
            chart_type_id: response.data.widgets[n].chart_type_id,
            metric_type_id: response.data.widgets[n].metric_type_id,
            table_id: response.data.widgets[n].table_id,
            field_id: response.data.widgets[n].field_id,
            data_type: response.data.widgets[n].data_type,
            is_all_time: response.data.widgets[n].is_all_time,
            start_date: start_date,
            end_date: end_date,
            show_range_option: response.data.widgets[n].show_range_option,
            show_compare_option: response.data.widgets[n].show_compare_option !== undefined ? Boolean(response.data.widgets[n].show_compare_option) : true,
            granularity: ['day', 'week', 'month'].includes(response.data.widgets[n].default_granularity) ? response.data.widgets[n].default_granularity : 'auto',
            applied_granularity: 'day',
            activity_source_id: response.data.widgets[n].activity_source_id,
            event_limit: response.data.widgets[n].event_limit,
            time_period_type_id: Number(time_period_type_id),
            y_axis_dimension: _.find(response.data.widgets[n].table_id === 17 ? this.purchaseMeasurements : this.measurements, {label: response.data.widgets[n].y_axis_dimension}),
            period: _.find(this.time_period_types, {id: time_period_type_id}),
            game_id: response.data.widgets[n].game_id || null,
            game_name: response.data.widgets[n].game_name || null,
            group_by_game: Boolean(response.data.widgets[n].group_by_game),
            company_user_id: response.data.widgets[n].company_user_id || null,
            group_by_user: Boolean(response.data.widgets[n].group_by_user),
            group_by_field: response.data.widgets[n].group_by_field || null,
            company_user_name: response.data.widgets[n].company_user_name || null,
            compare_mode: ['previous_periods', 'previous_years'].includes(response.data.widgets[n].default_compare_mode) ? response.data.widgets[n].default_compare_mode : 'none',
            compare_count: [2,3,4,5,6,7,8,9,10].includes(Number(response.data.widgets[n].default_compare_count)) ? Number(response.data.widgets[n].default_compare_count) : 2,
            comparison: null,
            field_name: response.data.widgets[n].field_name || null,
            show_description_label: response.data.widgets[n].show_description_label !== undefined ? Boolean(response.data.widgets[n].show_description_label) : true,
            allow_export: response.data.widgets[n].allow_export !== undefined ? Boolean(response.data.widgets[n].allow_export) : true,
            scope: response.data.widgets[n].scope === 'company' ? 'company' : 'group',
            company_group_ids: Array.isArray(response.data.widgets[n].company_group_ids) ? response.data.widgets[n].company_group_ids : [],
            purchase_item_filters: response.data.widgets[n].purchase_item_filters || null,
            new: false
          }

          this.layout.push(widgetItem);
        }

        await this.loadAllWidgets()

      } catch(err) {
        this.loading = false
        console.log(err)
        if (err.response && err.response.status == 401) {
          if(this.$route.query.id){
            // A dashboard id from the URL that is not visible under the active group (typically the
            // group was switched): drop the id and fall back to the group's pinned dashboard.
            this.$notify({
              type: 'warning',
              message: 'That dashboard is not available to you in the selected group. Showing your dashboards for this group.',
              icon: 'tim-icons icon-alert-circle-exc'
            })
            // The $route.query watcher reloads the group's dashboards once the query is empty.
            await this.$router.replace('/dashboards')
          } else {
            this.$router.push('/forbidden')
          }
        } else if(err.response && err.response.status == 400){
          this.$notify({ type: 'danger', message: typeof err.response.data === 'string' ? err.response.data : 'Something went wrong', icon: 'tim-icons icon-alert-circle-exc' })
        } else {
          this.$notify({ type: 'danger', message: 'Something went wrong', icon: 'tim-icons icon-alert-circle-exc' })
        }
      }
    },
    exportWidgetData(item, event){
      event.preventDefault()
      this.exportWidget = item
      this.exportFormat = 'csv'
      this.modals.export = true
    },
    async performExport(){
      if (!this.exportWidget) return

      try {
        const item = this.exportWidget
        // Generate filename
        const periodLabel = item.period ? item.period.name || item.period.label : 'All Time'
        const filename = `${item.title} - ${periodLabel}`.replace(/[^a-z0-9\s\-]/gi, '_')
        const timestamp = new Date().toISOString().split('T')[0]

        if (this.exportFormat === 'csv') {
          this.exportAsCSV(item, `${filename}_${timestamp}.csv`)
        } else if (this.exportFormat === 'json') {
          this.exportAsJSON(item, `${filename}_${timestamp}.json`)
        } else if (this.exportFormat === 'png') {
          await this.exportAsPNG(item, `${filename}_${timestamp}.png`)
        }

        this.modals.export = false
        this.exportWidget = null
        this.$notify({ type: 'success', message: 'Widget data exported successfully', icon: 'tim-icons icon-check-2' })
      } catch (err) {
        console.error('Export error:', err)
        this.$notify({ type: 'danger', message: 'Failed to export widget data', icon: 'tim-icons icon-alert-circle-exc' })
      }
    },
    exportAsCSV(item, filename){
      let csvContent = '';

      // Handle different widget types
      if (item.widget_type_id === 1) {
        // Chart widget - has labels and values
        if (!item.data || item.data.length === 0) {
          throw new Error('No data to export')
        }

        // CSV Header
        csvContent = 'Label,Value\n';

        // CSV Rows
        item.data.forEach(row => {
          const label = row.label || row.x || ''
          const value = row.value || row.y || 0
          csvContent += `"${label}",${value}\n`
        })
      } else if (item.widget_type_id === 3) {
        // Statistics widget
        if (item.group_by_game && item.data && Array.isArray(item.data)) {
          // Grouped by game
          csvContent = 'Game,Value\n';
          item.data.forEach(row => {
            csvContent += `"${row.game_name || 'Unknown'}",${row.statistic || 0}\n`
          })
        } else if (item.group_by_user && item.data && Array.isArray(item.data)) {
          // Grouped by staff member
          csvContent = 'Staff Member,Value\n';
          item.data.forEach(row => {
            csvContent += `"${row.user_name || 'Unknown'}",${row.statistic || 0}\n`
          })
        } else if (item.group_by_field && item.data && Array.isArray(item.data)) {
          // Grouped by field (currency, PM breakdown)
          csvContent = 'Group,Value\n';
          item.data.forEach(row => {
            csvContent += `"${row.label || 'Unknown'}",${row.statistic || 0}\n`
          })
        } else if (item.data && item.data.length > 0) {
          // Single statistic
          csvContent = 'Metric,Value\n';
          csvContent += `"${item.title}",${item.data[0].statistic || 0}\n`
        } else {
          throw new Error('No data to export')
        }
      } else if (item.widget_type_id === 2) {
        // Activity widget
        if (!item.data || item.data.length === 0) {
          throw new Error('No data to export')
        }

        // Get all unique keys from the data
        const keys = Object.keys(item.data[0] || {})
        csvContent = keys.join(',') + '\n'

        item.data.forEach(row => {
          const values = keys.map(key => {
            const val = row[key]
            // Wrap in quotes and escape existing quotes
            return typeof val === 'string' ? `"${val.replace(/"/g, '""')}"` : val
          })
          csvContent += values.join(',') + '\n'
        })
      }

      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', filename)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },
    exportAsJSON(item, filename){
      const exportData = {
        widget: {
          title: item.title,
          label: item.label,
          type: item.widget_type_id,
          period: item.period ? (item.period.name || item.period.label) : 'All Time',
          exported_at: new Date().toISOString()
        },
        data: item.data || null
      }

      if (item.game_name) {
        exportData.widget.game = item.game_name
      }

      if (item.company_user_name) {
        exportData.widget.staff_member = item.company_user_name
      }

      const jsonString = JSON.stringify(exportData, null, 2)
      const blob = new Blob([jsonString], { type: 'application/json' })
      const link = document.createElement('a')
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', filename)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },
    async exportAsPNG(item, filename){
      const widgetElement = document.querySelector(`[data-widget-id="widget-${item.id}"]`)

      if (!widgetElement) {
        throw new Error('Widget element not found')
      }

      // Capture the widget as canvas
      const canvas = await html2canvas(widgetElement, {
        backgroundColor: '#1e1e2e',
        scale: 2, // Higher quality
        logging: false,
        useCORS: true
      })

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', filename)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      }, 'image/png')
    }
  },
  beforeDestroy() {
    if(this.refreshTimer){ clearInterval(this.refreshTimer); this.refreshTimer = null }
    window.removeEventListener('resize', this.onViewportChange)
    },
  mounted() {
    this.onViewportChange()
    window.addEventListener('resize', this.onViewportChange)
    this.currency_symbol =  _.find(this.currencies, { code: JSON.parse(localStorage.getItem('group')).currency }).symbol
    this.date_format_cal = this.date_format
    this.date_format_cal =  this.date_format_cal.replaceAll('Y', 'y')
    this.date_format_cal = this.date_format_cal.replaceAll('D', 'd')
    if(JSON.parse(localStorage.getItem('group')).show_day_of_week){
      this.day_of_week = 'ddd, '
    }
      if (this.$route.path.includes('/fullscreen')) {
      this.fullscreen = true
    } else {
      this.fullscreen = false
    }
    this.getMainDashboard(this.$route.query.id)
  }
}
</script>

<style scoped>
.spin-animation {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
